import { isInternalUser } from '@/components/auth/RequireIsInternalUser';
import { ElectricianSignOff } from '@/components/MilestonePackagePartials/ElectricianSignOff';
import {
  useIsDomesticContentEligible,
  useIsDomesticContentEligibleAndAttested,
} from '@/helpers/checkDomesticContentEligibility';
import {
  isPackageEditable,
  PACKAGE_IN_REVIEW_QUEUE_ALERT_MESSAGE,
  shouldShowPackageUnderReviewAlert,
} from '@/helpers/packageEditability';
import { checkValueChangeTolerance } from '@/helpers/productionValueCheck';
import usePermissions from '@/hooks/usePermissions';
import { useToastSuccessSavedPackageMessage } from '@/hooks/useSavedPackageMessage';
import { useGetDocumentsQuery } from '@/services/documents';
import { Alert, Box, toast, useOpenClose } from '@palmetto/palmetto-components';
import { Formik, FormikValues } from 'formik';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router';

import {
  AccountMilestoneWithRequirements,
  AccountRequirementDocument,
  AccountRequirementStatus,
  AccountStipulationType,
  RequirementsWithRequirements,
  AdderDetails,
  AdderType,
  AdditionalCostAdder,
  AdditionalCostAdderType,
  BaseInstallPackageFileCategory,
  DCInstallPackageFileCategory,
  DeepPartial,
  DoePRInstallPackageFileCategory,
  InstallDocumentTypes,
  InstallPackage as IInstallPackage,
  InstallPackageFileCategory,
  MilestoneRequirement,
  MilestoneType,
  NON_TERMINAL_CHANGE_ORDER_STATUSES,
  ProgramType,
  PropertyType,
  propertyTypeLabelsMap,
  QueueDocumentResponse,
  QueueStatus,
  QueueType,
  QuoteStatus,
  UserPermissions,
  HardwareType,
  validateMonitoringIdFormat,
} from 'types';

import { mapAddersValuesFromDocument } from '../../helpers/addersHelpers';
import { convertToISODateString, convertToISODateTimeString, safeISOToFormDate } from '../../helpers/dates';
import { useSetPvSystemInitialValues } from '../../helpers/setPvSystemInitialValues';
import { useGetAccountQuery, useGetAccountStipulationsQuery } from '../../services/accounts';
import { useGetAddersQuery } from '../../services/adders';
import { useGetChangeOrdersQuery } from '../../services/changeOrders';
import { useGetCurrentContractQuery, useVoidContractMutation } from '../../services/contract';
import { isErrorWithData } from '../../services/helpers';
import { useSaveInstallPackageMutation, useSubmitInstallPackageMutation } from '../../services/installPackage';
import { useGetOrganizationDetailsQuery } from '../../services/organizations';
import { useGetQueuesByAccountIdQuery } from '../../services/queues';
import { useGetQuoteSettingsQuery, useGetQuotesQuery } from '../../services/quotes';
import { useCheckMonitoringAccessMutation } from '../../services/sundial';
import { useCreateSystemDesignMutation } from '../../services/systemDesign';
import { ReviewPackageActions } from '../../types/ReviewPackage';
import { ActionsList } from '../ActionsList';
import { useAuth } from '../auth/authProvider';
import { useIsOrgReadOnly } from '../auth/claimsUtils';
import { isPalmettoFinanceUser } from '../auth/RequirePalmettoFinanceUser';
import { EnhancedFormikForm } from '../EnhancedFormikForm';
import FormSection from '../Forms/FormSection';
import { MainContainer } from '../MainContainer';
import { Adders } from '../MilestonePackagePartials/Adders';
import { CommunityName } from '../MilestonePackagePartials/CommunityName';
import { Design } from '../MilestonePackagePartials/Design';
import { SolarReadinessAdder } from '../MilestonePackagePartials/doePr/SolarReadinessAdder';
import { Footer } from '../MilestonePackagePartials/Footer';
import { Installer } from '../MilestonePackagePartials/Installer';
import { InstallLoadProof } from '../MilestonePackagePartials/InstallLoadProof';
import { InstallPhotos } from '../MilestonePackagePartials/InstallPhotos';
import { MarketSpecificRequirements } from '../MilestonePackagePartials/MarketSpecificRequirements';
import { MonitoringActive } from '../MilestonePackagePartials/MonitoringActive';
import { Permit } from '../MilestonePackagePartials/Permit';
import { PropertyDetails } from '../MilestonePackagePartials/PropertyDetails';
import { PVSystemFormSection } from '../MilestonePackagePartials/PVSystemFormSection';
import { QueueBanner } from '../MilestonePackagePartials/QueueBanner';
import { SolarSerialNumber } from '../MilestonePackagePartials/SolarSerialNumber';
import { UtilityBill } from '../MilestonePackagePartials/UtilityBill';
import { NeedsAttestmentGuard } from '../NeedsAttestmentGuard/NeedsAttestmentGuard';
import { NotesDrawer } from '../NotesDrawer/NotesDrawer';
import { DiffErrorAlert } from '../ProductionValueCheck/DiffErrorAlert';
import { PreventSubmissionModal } from '../ProductionValueCheck/PreventSubmissionModal';
import { QueueFlagsProvider } from '../QueueFlags/QueueFlagsContext';
import { SkeletonBox } from '../SkeletonBox';

import { ContractKwhInfo } from './ContractKwhInfo';
import { DomesticContent } from './DomesticContent';
import { ExecutedEscrowAgreement } from './ExecutedEscrowAgreement';
import { useInstallPackageSchema } from './InstallPackage.utils';
import { InvoiceBomDocument } from './InvoiceBomDocument';
import { ReviewSlaCountdown } from './ReviewSlaCountdown';
import { MonitoringAccessConfirmationModal } from './Sundial/MonitoringAccessConfirmationModal';

interface InstallPackageProps {
  canSubmitInstallPackage: boolean;
}
type InstallPackageInverter = {
  manufacturer: string;
  model: string;
  count: number;
  vintage?: string;
};
export const SOLAR_READINESS_COST_INVOICE_ERR = `Amount must be greater than 0 when solar readiness adder invoice is uploaded`;

export const formatInstallPackagesData = (
  values: FormikValues,
  installerRequired: boolean,
): DeepPartial<IInstallPackage> => {
  const inverters = values?.inverterModels?.reduce((acc: InstallPackageInverter[], inverterModel: any) => {
    acc.push({
      manufacturer: values?.inverterManufacturer,
      model: inverterModel?.model,
      count: inverterModel?.count,
      ...(inverterModel?.vintage && inverterModel.vintage !== 'none' ? { vintage: inverterModel.vintage } : {}),
    });
    return acc;
  }, []);

  const storage = values?.storageModels?.map((storageModel: any) => {
    const storageObj: any = {
      manufacturer: values?.storageManufacturer,
      model: storageModel?.model,
      count: storageModel?.count,
    };
    if (storageModel?.vintage && storageModel.vintage !== 'none') {
      storageObj.vintage = storageModel.vintage;
    }
    return storageObj;
  });
  const adders = Object.keys(AdderType)
    .filter((type) => type !== AdderType.electricalUpgrade)
    .reduce((acc: AdderDetails[], adderType: string) => {
      if (Array.isArray(values[adderType])) {
        acc.push(...values[adderType]);
      } else if (values[adderType] && typeof values[adderType] === 'object' && values[adderType] !== null) {
        acc.push({ ...values[adderType], type: adderType });
      }

      return acc;
    }, []);

  const mountingHardware = values.mountingHardware?.map((hardware: any) => {
    return {
      ...hardware,
      mountingHardwareModels: hardware.mountingHardwareModels?.map((model: any) => {
        const { value, label, ...rest } = model; // Destructure and exclude `value` and `label`
        return rest;
      }),
    };
  });

  return {
    systemDesign: {
      panelManufacturer: values.panelManufacturer,
      panelModel: values.panelModel,
      totalPanelCount: values.totalPanelCount,
      inverterManufacturer: values.inverterManufacturer,
      inverters,
      mountingType: values.mountingType,
      mountingManufacturer: values.mountingManufacturer,
      mountingHardware: mountingHardware,
      ...(storage && storage.length > 0 ? { storage } : {}),
    },
    ...(installerRequired && {
      installer: {
        legalName: values.legalName,
        iccDocketNumber: values.iccDocketNumber,
        qualifiedPersonOnsiteFirstName: values.qualifiedPersonOnsiteFirstName,
        qualifiedPersonOnsiteLastName: values.qualifiedPersonOnsiteLastName,
        phoneNumber: values.phoneNumber,
        email: values.email,
        address: values.address,
        city: values.city,
        state: values.installerState,
      },
    }),
    monitoring: {
      monitoringSiteId: values.monitoringSiteId,
      batteryMonitoringSiteId: values.batteryMonitoringSiteId,
    },
    solarModuleSerialNumber: values.solarModuleSerialNumber,
    communityName: values.communityName,
    adders,
    designTool: values.designTool,
    permitSubmittedDate: {
      palmetto: values.permitSubmittedDate ? convertToISODateTimeString(values.permitSubmittedDate) : undefined,
    },
    permitApprovedDate: {
      palmetto: values.permitApprovedDate ? convertToISODateTimeString(values.permitApprovedDate) : undefined,
    },
    installScheduledDate: {
      palmetto: values.installScheduledDate ? convertToISODateTimeString(values.installScheduledDate) : undefined,
    },
    scheduledInspectionDate: {
      palmetto: values.scheduledInspectionDate
        ? convertToISODateTimeString(values.scheduledInspectionDate)
        : undefined,
    },
    expectedPTODate: {
      palmetto: values.expectedPTODate ? convertToISODateTimeString(values.expectedPTODate) : undefined,
    },
    utility: {
      tariffId: values.tariffId === '' ? null : values.tariffId,
    },
    electricianSignoffDate: {
      palmetto: values.electricianSignoffDate
        ? convertToISODateTimeString(values.electricianSignoffDate)
        : undefined,
    },
    executedEscrowAgreementDocumentDate: values.executedEscrowAgreementDocumentDate
      ? convertToISODateString(values.executedEscrowAgreementDocumentDate)
      : undefined,
    propertyType: values.propertyType?.value,
    isSecondSystem: values.isSecondSystem,
  };
};

export const InstallPackage = ({ canSubmitInstallPackage }: InstallPackageProps) => {
  const { enableQcPriority = false } = useFlags();
  const navigate = useNavigate();
  const { claims } = useAuth();
  const permissions = usePermissions();
  const isFinCoUser = isPalmettoFinanceUser(claims);
  const isLightReachQCTech = permissions.includes(UserPermissions.lightReachQCTech);

  const { id = '', action } = useParams<{ id: string; action: ReviewPackageActions }>();
  const { data: queuesData, isLoading: isQueueLoading } = useGetQueuesByAccountIdQuery({
    accountId: id,
    types: [QueueType.installPackage, QueueType.domesticContent],
  });
  const queue = queuesData?.find((q: QueueDocumentResponse) => q.type === QueueType.installPackage);
  const domesticContentQueue = queuesData?.find((q: QueueDocumentResponse) => q.type === QueueType.domesticContent);

  const { data: account, isLoading: isAccountLoading } = useGetAccountQuery(id);
  const { data: stipulations } = useGetAccountStipulationsQuery(id, { skip: !id });
  // Keyed on the stipulation (not a client flag): the stipulation only exists when the LD flag was
  // on at CO-create, so the approve button auto-respects rollout with no client-side flag read.
  const hasPendingChangeOrderHold =
    stipulations?.some(
      (stipulation) =>
        stipulation.stipulationType === AccountStipulationType.changeOrder && !stipulation.isSatisfied,
    ) ?? false;
  const programType = account?.programType;
  const installerRequired = account?.address?.state === 'IL';
  const isNewCommunity = programType === ProgramType.newCommunity;
  const isNewHomes = programType === ProgramType.newHomes;
  const isSolar = programType === ProgramType.solar;
  const isDoePR = programType === ProgramType.doePr;
  const state = account?.address?.state?.toUpperCase() || '';
  const isSolarPR = isSolar && state === 'PR';
  const isDcEligible = useIsDomesticContentEligible(account);
  const isDcEligibleAndAttested = useIsDomesticContentEligibleAndAttested(account);
  const isPackageInReviewView = action === ReviewPackageActions.review;
  const isPackageInEditView = action === ReviewPackageActions.edit;
  const isConsumerSolar = account?.programType === ProgramType.consumerSolar;
  const accountRequiresSolarModuleSerialNum = isNewCommunity || isNewHomes || isSolar || isConsumerSolar;
  const { requireUtilityBillForNtp = false } = useFlags();
  const ntpMilestone = account?.milestones?.find(
    (m: AccountMilestoneWithRequirements) => m.type === MilestoneType.noticeToProceed,
  );
  const utilityBillNtpReq = ntpMilestone?.requirements?.find(
    (r: AccountRequirementDocument) => r.type === MilestoneRequirement.utilityBill,
  );
  const isUtilityBillApprovedDuringNtp =
    requireUtilityBillForNtp &&
    (isSolar || isConsumerSolar || isDoePR) &&
    utilityBillNtpReq?.status === AccountRequirementStatus.completed;
  const hasCompletedUtilityBillRequirement = Boolean(
    account?.milestones?.some((m: AccountMilestoneWithRequirements) =>
      m.requirements?.some(
        (r: RequirementsWithRequirements) =>
          (r.type === MilestoneRequirement.utilityBill && r.status === AccountRequirementStatus.completed) ||
          r.requirements?.some(
            (child: AccountRequirementDocument) =>
              child.type === MilestoneRequirement.utilityBill && child.status === AccountRequirementStatus.completed,
          ),
      ),
    ),
  );
  let checkboxCategories: InstallPackageFileCategory[] = [
    ...Object.values(BaseInstallPackageFileCategory),
    DCInstallPackageFileCategory.invoice,
    DCInstallPackageFileCategory.billOfMaterials,
  ];
  const doePRCheckboxCategories = [...checkboxCategories, ...Object.values(DoePRInstallPackageFileCategory)];
  const showCheckboxes = isInternalUser(claims, true);
  const canEditPackage = isPackageEditable({
    queue,
    isFinCoUser,
  });

  const { data: quotes, isLoading: isQuotesLoading } = useGetQuotesQuery(
    { id, programType },
    { skip: !programType },
  );
  const { data: contract, isLoading: isContractLoading } = useGetCurrentContractQuery(id);
  const { data: addersData, isLoading: isAddersLoading } = useGetAddersQuery(id);
  const { data: changeOrders } = useGetChangeOrdersQuery(id);
  const hasPendingChangeOrder = Boolean(
    changeOrders?.some((changeOrder) => NON_TERMINAL_CHANGE_ORDER_STATUSES.includes(changeOrder.status)),
  );
  const [createSystemDesign, { isLoading: isSystemDesignLoading }] = useCreateSystemDesignMutation();
  const [voidContract] = useVoidContractMutation();
  const [triggerSubmit] = useSubmitInstallPackageMutation();
  const [triggerSave] = useSaveInstallPackageMutation();

  const { data: quoteSettings } = useGetQuoteSettingsQuery(
    { programType },
    {
      skip: !programType,
    },
  );
  const { data: organizationData } = useGetOrganizationDetailsQuery(
    {
      alias: account?.licensedOrganizationId ? account?.licensedOrganizationId : account?.organizationId,
    },
    { skip: !account?.licensedOrganizationId || !account?.organizationId },
  );

  const { data: solarReadinessInvoices = [] } = useGetDocumentsQuery(
    {
      accountId: id,
      documentTypeFilter: [InstallDocumentTypes.solarReadinessInvoice],
    },
    { skip: !isDoePR || !id },
  );

  const [checkMonitoringAccessConfirmation, { data: sundialResponse }] = useCheckMonitoringAccessMutation();

  const {
    isOpen: isMonitoringAccessConfirmationOpen,
    handleOpen: openMonitoringAccessConfirmation,
    handleClose: closeMonitoringAccessConfirmation,
  } = useOpenClose();

  const shouldDcReviewInformation =
    (domesticContentQueue?.status &&
      (isPackageInReviewView ||
        (isPackageInEditView && (isFinCoUser || domesticContentQueue.status === QueueStatus.rejected)))) ||
    domesticContentQueue?.status === QueueStatus.approved;
  const isDomesticContentPendingReview =
    isDcEligibleAndAttested && ![QueueStatus.approved, QueueStatus.rejected].includes(domesticContentQueue?.status);
  const attestmentType = account?.systemDesign?.domesticContentAttestment?.attestmentType;
  const itcQualificationResult = account?.systemDesign?.itcQualification?.result;
  const showDCUpdateSystemButton =
    isInternalUser(claims, true) ||
    (!isDoePR &&
      isDcEligible &&
      (!domesticContentQueue ||
        [QueueStatus.inProgress, QueueStatus.reReview, QueueStatus.rejected].includes(domesticContentQueue.status)));
  const isItcSystemDesignCompleted = Boolean(account?.systemDesign?.itcQualification);
  const isPvSystemReadOnly = !isDoePR && isDcEligibleAndAttested;
  const hasFeocException = Boolean(account?.systemDesign?.itcQualification?.result?.feocExceptionGranted);
  const showUnderReviewAlert = shouldShowPackageUnderReviewAlert({
    queue,
    isFinCoUser,
  });

  const [isVoiding, setIsVoiding] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const {
    isOpen: isPreventSubmissionOpen,
    handleOpen: setPreventSubmissionOpen,
    handleClose: setPreventSubmissionClose,
  } = useOpenClose();
  const [isNotesOpen, setIsNotesOpen] = useState(false);
  const isLoading =
    isAccountLoading ||
    isQuotesLoading ||
    isContractLoading ||
    isAddersLoading ||
    isQueueLoading ||
    isSystemDesignLoading;

  const installPackageSchema = useInstallPackageSchema({
    isDomesticContentEligible: isDcEligibleAndAttested,
    attestmentType,
    installerRequired,
    isFinCoUser,
    accountRequiresSolarModuleSerialNum,
    programType,
  });

  if (isDcEligibleAndAttested) {
    checkboxCategories = [
      ...checkboxCategories,
      DCInstallPackageFileCategory.domesticContentDocuments,
      DCInstallPackageFileCategory.incomingDirectPayInvoice,
    ];
  }

  useEffect(() => {
    if (!action || !ReviewPackageActions[action]) {
      navigate(`/accounts/${id}`, { replace: true });
    }
    if (isPackageInReviewView && (!isFinCoUser || !isLightReachQCTech)) {
      if (queue) {
        navigate(`/accounts/${id}/installation-package/edit`, { replace: true });
      } else {
        navigate(`/accounts/${id}/installation-package/create`, { replace: true });
      }
    }
  }, [id, navigate, action, isFinCoUser, isLightReachQCTech, queue, isPackageInReviewView]);

  const accountInitialValues = {
    lseId: account?.utility?.lseId,
    monitoringSiteId: account?.monitoringSiteId,
    state: account.address.state,
    tariffId: account?.utility?.tariffId || '',
    designTool: account.designTool,
    batteryMonitoringSiteId: account.batteryMonitoringSiteId,
    solarModuleSerialNumber: account?.solarModuleSerialNumber,
    communityName: account?.communityName,
    legalName: account.installer?.legalName ? account.installer?.legalName : organizationData?.name,
    iccDocketNumber: account.installer?.iccDocketNumber ? account.installer?.iccDocketNumber : '',
    qualifiedPersonOnsiteFirstName: account.installer?.qualifiedPersonOnsiteFirstName
      ? account.installer?.qualifiedPersonOnsiteFirstName
      : '',
    qualifiedPersonOnsiteLastName: account.installer?.qualifiedPersonOnsiteLastName
      ? account.installer?.qualifiedPersonOnsiteLastName
      : '',
    phoneNumber: account.installer?.phoneNumber ? account.installer?.phoneNumber : organizationData?.phoneNumber,
    email: account.installer?.email ? account.installer?.email : organizationData?.email,
    address: account.installer?.address ? account.installer?.address : organizationData?.address,
    city: account.installer?.city ? account.installer?.city : organizationData?.city,
    installerState: account.installer?.state ? account.installer?.state : organizationData?.state,
    permitApprovedDate: safeISOToFormDate(account?.permitApprovedDate?.palmetto),
    permitSubmittedDate: safeISOToFormDate(account?.permitSubmittedDate?.palmetto),
    installScheduledDate: safeISOToFormDate(account?.installScheduledDate?.palmetto),
    scheduledInspectionDate: safeISOToFormDate(account?.scheduledInspectionDate?.palmetto),
    expectedPTODate: safeISOToFormDate(account?.expectedPTODate?.palmetto),
    electricianSignoffDate: safeISOToFormDate(account?.electricianSignoffDate?.palmetto),
    executedEscrowAgreementDocumentDate: safeISOToFormDate(account?.executedEscrowAgreementDocumentDate),
    storage: account?.systemDesign?.storage,
    programType,
    propertyType: account?.propertyType
      ? {
          label: propertyTypeLabelsMap[account.propertyType as PropertyType],
          value: account.propertyType,
        }
      : undefined,
    isSecondSystem: account?.isSecondSystem ?? false,
  };
  const pvSystemInitialValues = useSetPvSystemInitialValues(account);
  const contractQuote = (quotes || []).find((element: any) => element.id === contract?.quoteId);
  const firstYearContracted = contractQuote?.systemFirstYearProductionKwh || 0;
  const firstYearInstalled = account?.systemDesign?.systemFirstYearProductionKwh || 0;
  const maxKwh = Math.max(firstYearInstalled, firstYearContracted);
  const minKwh = Math.min(firstYearInstalled, firstYearContracted);
  const difference = (maxKwh - minKwh) / maxKwh;
  const toleranceRate = checkValueChangeTolerance({
    newValue: firstYearInstalled,
    originalValue: firstYearContracted,
    settings: quoteSettings,
  });

  //If it's already contracted, we should display related
  //UI elements.
  const hasContracted = firstYearContracted > 0;

  const activeQuote = quotes?.find((quote) => quote.status === QuoteStatus.active);

  const addersInitialValues = mapAddersValuesFromDocument(addersData?.adders, activeQuote?.adders);

  const allowsAction = useIsOrgReadOnly();

  const triggerToastSuccessMessage = useToastSuccessSavedPackageMessage(id, MilestoneType.install);

  if (isDoePR) {
    const solarReadinessValsSavedOnAccountAdders = addersData?.adders.find(
      (adder: AdderDetails) => adder.type === AdderType.solarReadiness,
    );

    // Below is for legacy accounts when the solarReadinessAdder wasn't being saved in the accountAdders collection
    const solarReadinessValOnQuote = activeQuote?.additionalCostAdders.find(
      (adder: AdditionalCostAdder) => adder.type === AdditionalCostAdderType.solarReadiness,
    );

    const initialSolarReadinessValsToUse = solarReadinessValsSavedOnAccountAdders || solarReadinessValOnQuote;

    if (initialSolarReadinessValsToUse) {
      addersInitialValues.solarReadiness = {
        cost: initialSolarReadinessValsToUse.cost || 0,
        reasonOptionsSelected: initialSolarReadinessValsToUse.reasonOptionsSelected || [],
        otherReasonDetails: initialSolarReadinessValsToUse.otherReasonDetails || '',
      } as unknown as AdderDetails[];
    } else {
      addersInitialValues.solarReadiness = {
        cost: 0,
        reasonOptionsSelected: [],
        otherReasonDetails: '',
      } as unknown as AdderDetails[];
    }
  }
  const canSubmitReview = isPackageInReviewView && queue;
  const initialValues = {
    ...accountInitialValues,
    ...addersInitialValues,
    ...pvSystemInitialValues,
  };

  const handleSubmit = useCallback(
    async (values: FormikValues, { setSubmitting }: any) => {
      if (!toleranceRate.valid && !account?.systemDesign?.toleranceException) {
        setPreventSubmissionOpen();
        setSubmitting(false);
        return;
      }
      try {
        const formattedData = formatInstallPackagesData(values, installerRequired) as IInstallPackage;
        await triggerSubmit({
          accountId: id,
          installPackage: formattedData,
        }).unwrap();
        toast.success('Install Package Submitted');
        // TODO: Re-enable Hardware Lookup warnings when this behavior is required
        navigate(`/accounts/${id}`);
      } catch (e: any) {
        toast.error(e?.data?.message || 'Error submitting install package');
      } finally {
        setSubmitting(false);
      }
    },
    [
      id,
      triggerSubmit,
      navigate,
      setPreventSubmissionOpen,
      toleranceRate.valid,
      installerRequired,
      account?.systemDesign?.toleranceException,
    ],
  );

  const handleMonitoringAccessConfirmation = useCallback(
    async (values: FormikValues, { setSubmitting }: any) => {
      const batteryAndInverterMonitoringIds = [
        ...(values?.batteryMonitoringSiteId
          ? [{ id: values.batteryMonitoringSiteId, type: HardwareType.battery }]
          : []),
        ...(values?.monitoringSiteId ? [{ id: values.monitoringSiteId, type: HardwareType.inverter }] : []),
      ];
      try {
        const sundialActualResponse = await checkMonitoringAccessConfirmation({
          accountId: id,
          monitoringIds: batteryAndInverterMonitoringIds,
        }).unwrap();
        if (!sundialActualResponse?.allVerified && batteryAndInverterMonitoringIds.length !== 0) {
          setSubmitting(false);
          openMonitoringAccessConfirmation();
        } else {
          await handleSubmit(values, { setSubmitting });
        }
      } catch (e) {
        console.error('Error fetching monitoring access confirmation', e);
        toast.error('Error fetching monitoring access confirmation. Please try again later.');
        setSubmitting(false);
        openMonitoringAccessConfirmation();
      }
    },
    [checkMonitoringAccessConfirmation, id, openMonitoringAccessConfirmation, handleSubmit],
  );
  const shouldShowInvoiceBomSection = !isDcEligibleAndAttested;

  async function requestVoid() {
    if (!contract?.id) return;
    setIsVoiding(true);
    try {
      await voidContract({
        accountId: id,
        contractId: contract.id,
      }).unwrap();

      navigate(`/accounts/${id}`, {
        state: {
          justVoidedContract: true,
          justUpdated1yEstimate: true,
        },
      });
    } catch (error) {
      console.error({ error });
      if (error) {
        toast.error('An error has occurred trying to void the contract. Please try again.');
      } else {
        toast.error('An error has occurred. Please try again later.');
      }
    }
    setIsVoiding(false);
  }

  const handleToleranceExceptionModal = async (note: string): Promise<boolean> => {
    if (!note || note.trim() === '' || note === undefined) {
      toast.error('Notes are required to grant a tolerance exception');
      return false;
    }
    try {
      await createSystemDesign({
        accountId: id,
        systemDesign: { ...account.systemDesign, toleranceException: { note } },
        programType: programType as ProgramType,
      }).unwrap();

      toast.success('Production Exception Applied');
      return false;
    } catch (e) {
      toast.error('Something went wrong. Please try again.');
      console.error({ e });
      return false;
    }
  };

  const loadingState = {
    isAccountLoading,
    isVoiding,
  };

  const handleSave = async ({ accountId, data }: { accountId: string; data: FormikValues }) => {
    setIsSaving(true);

    if (isDoePR && data.solarReadiness && solarReadinessInvoices.length > 0) {
      if (data.solarReadiness.cost === undefined || data.solarReadiness.cost <= 0) {
        toast.error(SOLAR_READINESS_COST_INVOICE_ERR);
        setIsSaving(false);
        throw new Error(SOLAR_READINESS_COST_INVOICE_ERR);
      }
    }

    const formattedData = formatInstallPackagesData(data, installerRequired);
    try {
      await triggerSave({ accountId, savePackage: formattedData }).unwrap();
      triggerToastSuccessMessage();
      // TODO: Re-enable Hardware Lookup warnings when this behavior is required
    } catch (e: any) {
      console.error(e);
      if (isErrorWithData(e)) {
        const errorMessage = e.data.message;
        throw new Error(errorMessage);
      } else {
        console.error(e);
        throw new Error('Error saving Install Package');
      }
    } finally {
      setIsSaving(false);
    }
  };

  const handleValidation = (values: any) => {
    const errors = {} as any;
    const monitoringIdFormat = validateMonitoringIdFormat(values.monitoringSiteId, values.inverterManufacturer);
    if (values.monitoringSiteId && !monitoringIdFormat.valid) {
      errors.monitoringSiteId = `Monitoring Site ID must match the required format: ${monitoringIdFormat.expectedFormat}.`;
    }

    if (!values.designTool) {
      errors.designTool = 'Design Tool is required';
    }

    if (isDoePR && values.solarReadiness && solarReadinessInvoices.length > 0) {
      if (values.solarReadiness.cost === undefined || values.solarReadiness.cost <= 0) {
        if (!errors.solarReadiness) {
          errors.solarReadiness = {};
        }
        errors.solarReadiness.cost = SOLAR_READINESS_COST_INVOICE_ERR;
      }
    }

    if (installerRequired) {
      if (!values.address) {
        errors.address = 'Installer Address is required';
      }
      if (!values.city) {
        errors.city = 'Installer City is required';
      }
      if (!values.installerState) {
        errors.installerState = 'Installer State is required';
      }
      if (!values.legalName) {
        errors.legalName = 'Installer Legal Name is required';
      }
      if (!values.iccDocketNumber) {
        errors.iccDocketNumber = 'Installer ICC Docket Number is required';
      }
      if (!values.qualifiedPersonOnsiteFirstName) {
        errors.qualifiedPersonOnsiteFirstName = 'Installer Qualified Person Onsite First Name is required';
      }
      if (!values.qualifiedPersonOnsiteLastName) {
        errors.qualifiedPersonOnsiteLastName = 'Installer Qualified Person Onsite Last Name is required';
      }
      if (!values.phoneNumber) {
        errors.phoneNumber = 'Installer Phone Number is required';
      }
      if (!values.email) {
        errors.email = 'Installer Email is required';
      }
    }

    //TODO: keep only accountRequiresSolarModuleSerialNum check shortly after post launch of FIN-2462
    if (
      (isDcEligibleAndAttested || accountRequiresSolarModuleSerialNum) &&
      (!isNewCommunity || isDcEligibleAndAttested)
    ) {
      if (!values.solarModuleSerialNumber) {
        errors.solarModuleSerialNumber = 'Serial Number is a required field';
      }
    }

    if (isNewCommunity && !values.communityName?.trim()) {
      errors.communityName = 'Community Name is a required field';
    }

    return errors;
  };
  const queueHasBeenApprovedOrConditionallyApproved =
    queue && (queue.status === QueueStatus.conditionallyApproved || queue.status === QueueStatus.approved);
  const queueStatusIsApprovedOrConditionallyApprovedAndIsNotFinCoUser: boolean =
    !isFinCoUser && queueHasBeenApprovedOrConditionallyApproved;

  if (isLoading) {
    return (
      <MainContainer>
        <FormSection title="Loading...">
          <SkeletonBox height="25px" width="100" />
        </FormSection>
      </MainContainer>
    );
  }

  return (
    <QueueFlagsProvider queueId={queue?.id}>
      <NotesDrawer isOpen={isNotesOpen} setIsOpen={setIsNotesOpen} queueType={QueueType.installPackage} />
      <NeedsAttestmentGuard>
        <MainContainer>
          <Formik
            initialValues={initialValues}
            validationSchema={installPackageSchema}
            onSubmit={handleMonitoringAccessConfirmation}
            enableReinitialize={true}
            validateOnChange={false}
            validate={handleValidation}
          >
            <EnhancedFormikForm>
              {enableQcPriority &&
                queue?.reviewSla?.current?.targetDate &&
                !queue.reviewSla.current.decisionDate && (
                  <Box justifyContent="flex-end" gap="sm" padding="0 0 md 0">
                    {isFinCoUser && queue?.reviewSla?.reviewPriorityTier != null && (
                      <Box fontSize="sm" fontWeight="medium" alignItems="flex-end">
                        Tier: {queue.reviewSla.reviewPriorityTier}
                      </Box>
                    )}
                    <ReviewSlaCountdown targetDate={queue.reviewSla.current.targetDate} />
                  </Box>
                )}
              <ActionsList
                milestoneType={MilestoneType.install}
                accountId={queue?.account?.id}
                canSubmitPackage={canSubmitInstallPackage && allowsAction && !hasPendingChangeOrder}
                isLoading={isLoading || isSaving || false}
                isFirstSubmission={!queue}
                canSubmitReview={canSubmitReview && allowsAction}
                onSave={handleSave}
                disabled={
                  queueStatusIsApprovedOrConditionallyApprovedAndIsNotFinCoUser || !allowsAction || !canEditPackage
                }
                disableApproval={isDomesticContentPendingReview || hasPendingChangeOrderHold}
                initialValues={initialValues}
                onSubmit={handleMonitoringAccessConfirmation}
                queue={queue}
                subQueueInfo={{
                  type: domesticContentQueue ? QueueType.domesticContent : undefined,
                  status: domesticContentQueue?.status,
                }}
              />
              {queue && (queue.status === QueueStatus.rejected || isFinCoUser) ? (
                <QueueBanner
                  setIsNotesOpen={setIsNotesOpen}
                  queueType={QueueType.installPackage}
                  isFinCoUser={isFinCoUser}
                  is8090Project={Boolean(queue?.aiPackageReviewStatus)}
                />
              ) : null}
              {hasPendingChangeOrder ? (
                <Alert
                  variant="warning"
                  title="Change Order Pending"
                  message="A change order is pending for this account. It must be resolved (approved or declined) before the install package can be submitted. Progress can still be saved."
                  hasIcon
                />
              ) : null}
              {showUnderReviewAlert ? (
                <Alert
                  variant="warning"
                  title="Install Package In Review Queue"
                  message={PACKAGE_IN_REVIEW_QUEUE_ALERT_MESSAGE}
                  hasIcon
                />
              ) : null}
              {isPackageInReviewView && !canSubmitReview ? (
                <Alert
                  variant="warning"
                  title="Install Package Not Submitted"
                  message="This install package is not ready to be reviewed yet. It has not been submitted."
                  hasIcon
                />
              ) : null}
              {hasContracted && !toleranceRate.valid && !account?.systemDesign?.toleranceException && (
                <DiffErrorAlert
                  requestVoid={requestVoid}
                  errorObject={{
                    production: {
                      percentErrorDiff: difference,
                      errorKwH: maxKwh - minKwh,
                    },
                  }}
                  loadingState={loadingState}
                  handleToleranceExceptionModal={handleToleranceExceptionModal}
                />
              )}
              {hasContracted && (
                <Box
                  padding={{ base: '0 xl xl', tablet: '0 0 xl 0' }}
                  borderColor="separator"
                  borderWidth="0 0 xs 0"
                >
                  <ContractKwhInfo
                    firstYearContracted={firstYearContracted}
                    firstYearInstalled={firstYearInstalled}
                    toleranceException={account?.systemDesign?.toleranceException}
                    outOfRange={!toleranceRate.valid}
                    programType={programType}
                  />
                </Box>
              )}

              {(isNewHomes || isNewCommunity) && <ExecutedEscrowAgreement isPackageEditable={canEditPackage} />}

              {isNewCommunity && <CommunityName isPackageEditable={canEditPackage} />}

              {/* TODO:remove below line shortly after post launch of FIN-2462 */}
              {(isDcEligibleAndAttested || accountRequiresSolarModuleSerialNum) && (
                <SolarSerialNumber isPackageEditable={canEditPackage} />
              )}
              {isDcEligibleAndAttested && (
                <DomesticContent
                  shouldDisplayDomesticContentReviewInformation={shouldDcReviewInformation}
                  attestmentType={attestmentType}
                  domesticContentQualificationResult={itcQualificationResult}
                  showCheckboxes={showCheckboxes}
                  checkboxCategories={checkboxCategories}
                  isPackageEditable={canEditPackage}
                />
              )}
              <PVSystemFormSection
                programType={programType}
                showUpdateSystemButton={showDCUpdateSystemButton}
                attestmentType={attestmentType}
                readOnly={isPvSystemReadOnly}
                itcQualificationResult={itcQualificationResult}
                isItcSystemDesignCompleted={isItcSystemDesignCompleted}
                hasFeocException={hasFeocException}
                orgAlias={account?.organizationId}
                isPackageEditable={canEditPackage}
              />
              {shouldShowInvoiceBomSection && (
                <InvoiceBomDocument
                  showCheckboxes={showCheckboxes}
                  checkboxCategories={checkboxCategories}
                  isPackageEditable={canEditPackage}
                />
              )}
              {installerRequired && <Installer isPackageEditable={canEditPackage} />}
              {(isPackageInReviewView || queueHasBeenApprovedOrConditionallyApproved) &&
                (isSolar || isNewHomes || isNewCommunity) && (
                  <PropertyDetails isFinCoUser={isFinCoUser} accountId={id} propertyAddress={account?.address} />
                )}
              <MonitoringActive
                adders={activeQuote?.adders}
                isMonitoringRequired={!isNewCommunity}
                accountId={id}
                milestoneType={MilestoneType.install}
                isPackageEditable={canEditPackage}
              />
              <Adders
                adders={activeQuote?.adders}
                flags={queue?.flags}
                isLoading={isAddersLoading}
                programType={programType}
                readOnly={isPvSystemReadOnly}
                orgAlias={account?.organizationId}
                isPackageEditable={canEditPackage}
              />
              {!isNewCommunity && (
                <Permit
                  allowArchive={!queueStatusIsApprovedOrConditionallyApprovedAndIsNotFinCoUser}
                  showCheckboxes={showCheckboxes}
                  checkboxCategories={checkboxCategories}
                  isPackageEditable={canEditPackage}
                />
              )}
              <UtilityBill
                account={account}
                originContext={QueueType.installPackage}
                allowArchive={!queueStatusIsApprovedOrConditionallyApprovedAndIsNotFinCoUser}
                showCheckboxes={showCheckboxes}
                checkboxCategories={checkboxCategories}
                isPackageEditable={canEditPackage}
                isUtilityBillApprovedDuringNtp={isUtilityBillApprovedDuringNtp}
                hideDocumentUpload={hasCompletedUtilityBillRequirement}
              />
              <MarketSpecificRequirements
                originContext={QueueType.installPackage}
                allowArchive={!queueStatusIsApprovedOrConditionallyApprovedAndIsNotFinCoUser}
                showCheckboxes={showCheckboxes}
                checkboxCategories={checkboxCategories}
                milestoneType={MilestoneType.install}
                isPackageEditable={canEditPackage}
              />
              <Design
                allowArchive={!queueStatusIsApprovedOrConditionallyApprovedAndIsNotFinCoUser}
                showCheckboxes={showCheckboxes}
                checkboxCategories={checkboxCategories}
                isNewCommunity={isNewCommunity}
                isPackageEditable={canEditPackage}
              />
              <InstallPhotos
                allowArchive={!queueStatusIsApprovedOrConditionallyApprovedAndIsNotFinCoUser}
                showCheckboxes={showCheckboxes}
                checkboxCategories={checkboxCategories}
                programType={programType}
                isPackageEditable={canEditPackage}
              />
              {isDoePR && (
                <>
                  <InstallLoadProof
                    showCheckboxes={showCheckboxes}
                    checkboxCategories={doePRCheckboxCategories}
                    isPackageEditable={canEditPackage}
                  />
                  <ElectricianSignOff
                    showCheckboxes={showCheckboxes}
                    checkboxCategories={doePRCheckboxCategories}
                    isPackageEditable={canEditPackage}
                  />
                  <SolarReadinessAdder
                    showCheckboxes={showCheckboxes}
                    checkboxCategories={doePRCheckboxCategories}
                    isPackageEditable={canEditPackage}
                  />
                </>
              )}
              {isSolarPR && (
                <ElectricianSignOff
                  showCheckboxes={showCheckboxes}
                  checkboxCategories={checkboxCategories}
                  isPackageEditable={canEditPackage}
                />
              )}
              <Footer
                canSubmitPackage={canSubmitInstallPackage && allowsAction && !hasPendingChangeOrder}
                disableAllApprovals={isDomesticContentPendingReview || hasPendingChangeOrderHold}
                hasPendingChangeOrder={hasPendingChangeOrderHold}
                isLoading={isLoading || isSaving}
                isFirstSubmission={!queue}
                canSubmitReview={canSubmitReview && allowsAction}
                onSave={handleSave}
                disabled={
                  queueStatusIsApprovedOrConditionallyApprovedAndIsNotFinCoUser || !allowsAction || !canEditPackage
                }
                subQueueInfo={{
                  type: domesticContentQueue ? QueueType.domesticContent : undefined,
                  status: domesticContentQueue?.status,
                }}
              />
              <MonitoringAccessConfirmationModal
                monitoringAccessConfirmationInfo={sundialResponse?.results}
                handleSubmit={handleSubmit}
                isOpen={isMonitoringAccessConfirmationOpen}
                onDismiss={closeMonitoringAccessConfirmation}
                isLoading={isLoading || isSaving}
              />
            </EnhancedFormikForm>
          </Formik>
          <PreventSubmissionModal isOpen={isPreventSubmissionOpen} onDismiss={setPreventSubmissionClose} />
        </MainContainer>
      </NeedsAttestmentGuard>
    </QueueFlagsProvider>
  );
};
